/**
 * usePolicyTableState
 * Shared state management hook for policy tables using useReducer
 * Consolidates common state: tab selection, search, priority filter
 */

import { useReducer, useMemo, useCallback } from 'react';
import type { MessageBarIntent } from '@fluentui/react-components';
import { PolicyTabType, DashboardPolicy, TenantPolicy } from './types';
import { filterPoliciesByTab, countPoliciesByType, filterPolicies } from './utils';

// ── State & Actions ─────────────────────────────────────────────────────

export interface DeployMessage {
  intent: MessageBarIntent;
  title: string;
  body: string;
}

interface PolicyTableState {
  selectedTab: PolicyTabType;
  search: string;
  statusFilter: string;   // DeploymentStatus | 'All'
  deployMessage: DeployMessage | null;
}

type PolicyTableAction =
  | { type: 'SET_TAB'; payload: PolicyTabType }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_STATUS'; payload: string }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_DEPLOY_MESSAGE'; payload: DeployMessage | null };

function policyTableReducer(state: PolicyTableState, action: PolicyTableAction): PolicyTableState {
  switch (action.type) {
    case 'SET_TAB':
      return { ...state, selectedTab: action.payload };
    case 'SET_SEARCH':
      return { ...state, search: action.payload };
    case 'SET_STATUS':
      return { ...state, statusFilter: action.payload };
    case 'RESET_FILTERS':
      return { ...state, search: '', statusFilter: 'All' };
    case 'SET_DEPLOY_MESSAGE':
      return { ...state, deployMessage: action.payload };
    default:
      return state;
  }
}

// ── Hook ────────────────────────────────────────────────────────────────

interface UsePolicyTableStateOptions<T extends DashboardPolicy | TenantPolicy> {
  policies: T[];
  initialTab?: PolicyTabType;
  /** Optional server-side count of DLP policies. If provided, overrides the client-side tally on `policies`. */
  dlpTotalCount?: number;
  /** Optional server-side count of AutoLabel policies. If provided, overrides the client-side tally on `policies`. */
  autoLabelTotalCount?: number;
}

interface UsePolicyTableStateReturn<T extends DashboardPolicy | TenantPolicy> {
  selectedTab: PolicyTabType;
  setSelectedTab: (tab: PolicyTabType) => void;
  search: string;
  setSearch: (search: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  deployMessage: DeployMessage | null;
  setDeployMessage: (msg: DeployMessage | null) => void;
  tabPolicies: T[];
  filteredPolicies: T[];
  dlpCount: number;
  autoLabelCount: number;
  resetFilters: () => void;
}

export function usePolicyTableState<T extends DashboardPolicy | TenantPolicy>({
  policies,
  initialTab = 'DLP',
  dlpTotalCount,
  autoLabelTotalCount,
}: UsePolicyTableStateOptions<T>): UsePolicyTableStateReturn<T> {
  const [state, dispatch] = useReducer(policyTableReducer, {
    selectedTab: initialTab,
    search: '',
    statusFilter: 'All',
    deployMessage: null,
  });

  const setSelectedTab = useCallback((tab: PolicyTabType) => {
    dispatch({ type: 'SET_TAB', payload: tab });
  }, []);

  const setSearch = useCallback((search: string) => {
    dispatch({ type: 'SET_SEARCH', payload: search });
  }, []);

  const setStatusFilter = useCallback((status: string) => {
    dispatch({ type: 'SET_STATUS', payload: status });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: 'RESET_FILTERS' });
  }, []);

  const setDeployMessage = useCallback((msg: DeployMessage | null) => {
    dispatch({ type: 'SET_DEPLOY_MESSAGE', payload: msg });
  }, []);

  const tabPolicies = useMemo(
    () => filterPoliciesByTab(policies, state.selectedTab),
    [policies, state.selectedTab],
  );

  const { dlpCount: clientDlpCount, autoLabelCount: clientAutoLabelCount } = useMemo(
    () => countPoliciesByType(policies),
    [policies],
  );

  // Prefer server-provided totals (independent of policyType filter / current page);
  // fall back to the client-side tally of whatever's currently loaded.
  const dlpCount = typeof dlpTotalCount === 'number' ? dlpTotalCount : clientDlpCount;
  const autoLabelCount =
    typeof autoLabelTotalCount === 'number' ? autoLabelTotalCount : clientAutoLabelCount;

  const filteredPolicies = useMemo(
    () => filterPolicies(tabPolicies, state.search, state.statusFilter),
    [tabPolicies, state.search, state.statusFilter],
  );

  return {
    selectedTab: state.selectedTab,
    setSelectedTab,
    search: state.search,
    setSearch,
    statusFilter: state.statusFilter,
    setStatusFilter,
    deployMessage: state.deployMessage,
    setDeployMessage,
    tabPolicies,
    filteredPolicies,
    dlpCount,
    autoLabelCount,
    resetFilters,
  };
}
